import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Webinar } from "../../types/webinar.types";
import { webinarApi } from "../../services/webinar.service";
import { useT, useI18n } from "../../i18n";

const formatDate = (value: string | undefined, locale: string) => {
  if (!value) return "";
  return new Date(value).toLocaleString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const WebinarCard: React.FC<{
  item: Webinar;
  onWatch: (id: string) => void;
}> = ({ item, onWatch }) => {
  const { t, code } = useI18n();
  return (
    <li className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50/50 transition-colors">
      <div className="flex items-center space-x-4">
        <div className="p-3 rounded-lg bg-purple-50">
          <i className="fa-solid fa-play text-purple-600 text-xl"></i>
        </div>
        <div>
          <span className="text-base font-medium text-gray-800 block">
            {item.title}
          </span>
          <span className="text-sm text-gray-500">
            {t("webinars.recordedOn", { date: formatDate(item.createdAt, code) })}
          </span>
          {item.description && (
            <p className="text-sm text-gray-500 mt-0.5 max-w-xl">
              {item.description}
            </p>
          )}
        </div>
      </div>
      <button
        onClick={() => onWatch(item.id)}
        className="px-4 py-2 flex items-center text-white rounded-lg transition-colors text-sm font-medium bg-[#111827] hover:bg-gray-800"
      >
        <i className="fa-solid fa-play mr-2"></i> {t("webinars.watch")}
      </button>
    </li>
  );
};

export default function WebinarPage() {
  const navigate = useNavigate();
  const t = useT();
  const [webinars, setWebinars] = useState<Webinar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWebinars = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await webinarApi.getAllWebinars();
        setWebinars(response.data || []);
      } catch (err: any) {
        setError(
          t("webinars.loadFailed")
        );
      } finally {
        setLoading(false);
      }
    };
    fetchWebinars();
  }, []);

  const handleWatch = (id: string) => navigate(`/webinar/${id}`);

  return (
    <div className="flex flex-col flex-1 h-full">
      {/* Header */}
      <div className="bg-white px-8 pt-4 pb-2 border-b border-gray-200 flex flex-col gap-1 relative sticky top-0 z-20">
        <h1 className="text-2xl font-bold text-gray-900">{t("webinars.title")}</h1>
        <p className="text-sm text-gray-500 max-w-2xl">
          {t("webinars.subtitle")}
        </p>
      </div>

      {/* Main content */}
      <div className="flex-1 px-8 pb-8">
        <div className="bg-white rounded-xl shadow p-8 mt-4">
          <p className="text-gray-700 leading-relaxed">
            {t("webinars.intro")}
          </p>

          {loading && (
            <p className="mt-6 text-sm text-gray-500">{t("webinars.loading")}</p>
          )}
          {error && <p className="mt-6 text-sm text-red-600">{error}</p>}
          {!loading && !error && webinars.length === 0 && (
            <p className="mt-6 text-sm text-gray-500">
              {t("webinars.empty")}
            </p>
          )}

          {/* Recorded Webinars */}
          {webinars.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <i className="fa-solid fa-play-circle text-purple-600 mr-2"></i>
                {t("webinars.recordedWebinars")}
              </h3>
              <ul className="space-y-4">
                {webinars.map((item) => (
                  <WebinarCard
                    key={item.id}
                    item={item}
                    onWatch={handleWatch}
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
